import React, { useState, useRef, useEffect } from 'react';
import sanitizeHtml from 'sanitize-html';
import { marked } from 'marked';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import {
  type InteractiveMapProps,
  type DrawerProps,
} from './interactive-map.types';
import {
  CloseButton,
  CloseSection,
  CloseText,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  ImageWrapper,
  MapContainer,
  PillTag,
  Pin,
  NavigationHint,
  BackgroundColor,
} from './interactive-map.styles';
import useWindowSize from '@hooks/use-window-size';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { GTM_ID } from '@common/constants/gtmIds';

const sanitizeAndParseMarkdown = (content: string) => {
  const rawMarkup = marked(content);
  return sanitizeHtml(rawMarkup, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'h1']),
    allowedAttributes: {
      a: ['href', 'name', 'target'],
      img: ['src', 'alt'],
    },
  });
};

export const MapDrawer = ({
  content,
  isOpen,
  onClose,
  isMobile,
}: DrawerProps) => {
  // Despite the z-index in the drawer, we had to toggle html body-scroll when the drawer is open.
  const toggleBodyScroll = (disable: boolean) => {
    if (disable) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  useEffect(() => {
    toggleBodyScroll(isOpen && isMobile);
    return () => toggleBodyScroll(false);
  }, [isOpen, isMobile]);

  return (
    <Drawer isOpen={isOpen} data-testid="map-drawer">
      <DrawerHeader>
        {content.pill && (
          <PillTag
            useDarkPillText={content.useDarkPillText}
            color={content.color}
            data-testid="drawer-pill"
          >
            {content.pill}
          </PillTag>
        )}
        <CloseSection onClick={isMobile ? onClose : () => {}}>
          <CloseText>Close</CloseText>
          <CloseButton
            onClick={onClose}
            data-testid="close-drawer-btn"
            aria-label="Close drawer"
            isMobile={isMobile}
          />
        </CloseSection>
      </DrawerHeader>
      <DrawerContent>
        <DrawerTitle
          color={content.color}
          useDarkPillText={content.useDarkPillText}
        >
          {content.title}
        </DrawerTitle>
        <div
          data-testid="markdown-content"
          dangerouslySetInnerHTML={{
            __html: sanitizeAndParseMarkdown(content.description),
          }}
        />
      </DrawerContent>
    </Drawer>
  );
};

export const InteractiveMap = ({
  backgroundColor,
  backgroundImage,
  mobileBackgroundImage,
  isFullWidth,
  interactiveImage,
  pinContentMapper,
  className,
}: InteractiveMapProps) => {
  const [selectedPin, setSelectedPin] = useState<string | null>(null);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [showNavigationHint, setShowNavigationHint] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const { width, height: windowHeight } = useWindowSize();
  const isMobile = !!width && width <= 768;

  const isGatsbyImage =
    interactiveImage.file.contentType !== 'image/gif' &&
    interactiveImage.file.contentType !== 'image/svg+xml';
  const image = isGatsbyImage ? getImage(interactiveImage) : null;

  const bgImage =
    isMobile && mobileBackgroundImage ? mobileBackgroundImage : backgroundImage;

  const isUnsupportedBgType =
    bgImage?.format === 'gif' || bgImage?.format === 'svg';
  const gatsbyBgImage = bgImage?.gatsbyImageData
    ? getImage(bgImage.gatsbyImageData)
    : null;

  const selectedPinContent = pinContentMapper.find(
    pin => pin.id === selectedPin
  );

  const handlePinClick = (pinId: string) => {
    setSelectedPin(pinId);
    setIsDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
    setTimeout(() => setSelectedPin(null), 300);
  };

  const renderImageContent = () => (
    <>
      {isGatsbyImage && image ? (
        <GatsbyImage image={image} alt={interactiveImage.description ?? ''} />
      ) : (
        <img
          src={interactiveImage.file.url}
          alt={interactiveImage.description || ''}
          data-testid="interactive-map-image"
          className={isMobile ? 'mobile-view' : ''}
        />
      )}
      {pinContentMapper.map(pin => (
        <Pin
          key={pin.id}
          data-testid={`map-pin-${pin.id}`}
          style={{
            left: `${pin.coordinates.x}%`,
            top: `${pin.coordinates.y}%`,
          }}
          color={pin.color}
          isActive={pin.id === selectedPin}
          onClick={() => handlePinClick(pin.id)}
          {...(pin.id !== selectedPin && { 'data-gtm-id': GTM_ID.IMAGE_PIN })}
          aria-label={`Open ${pin.title}`}
        />
      ))}
    </>
  );

  //image.height (set by getImage) gives a different height than the original uploaded size. same for image.width.
  // multiplying the scale with 1.5 is kind of a magic solution
  const originalHeight = interactiveImage?.file.details.image.height;
  const initialScale =
    !!windowHeight && originalHeight < windowHeight
      ? windowHeight / originalHeight
      : 1;

  return (
    <MapContainer
      className={className}
      ref={containerRef}
      isFullWidth={isFullWidth}
    >
      {(backgroundColor || backgroundImage || mobileBackgroundImage) && (
        <BackgroundColor backgroundColor={backgroundColor}>
          {bgImage &&
            (!isUnsupportedBgType && gatsbyBgImage ? (
              <GatsbyImage
                image={gatsbyBgImage}
                alt={bgImage.alt || ''}
                data-testid="background-gatsby-image"
              />
            ) : (
              <img
                src={bgImage.src}
                alt={bgImage.alt || ''}
                data-testid={
                  isUnsupportedBgType
                    ? 'background-image'
                    : 'background-fallback-image'
                }
              />
            ))}
        </BackgroundColor>
      )}
      <ImageWrapper isMobile={isMobile} isFullWidth={isFullWidth}>
        {isMobile ? (
          <>
            {showNavigationHint && (
              <NavigationHint>
                pinch to zoom and drag to navigate
              </NavigationHint>
            )}
            <TransformWrapper
              initialScale={initialScale}
              doubleClick={{ disabled: true }}
              limitToBounds={true}
              centerOnInit
              centerZoomedOut={false}
              onPanning={() => setShowNavigationHint(false)}
              onPinching={() => setShowNavigationHint(false)}
            >
              <TransformComponent
                wrapperClass="transform-wrapper"
                contentClass="transform-content"
                wrapperStyle={{
                  maxWidth: '100%',
                  height: '100%',
                }}
              >
                {renderImageContent()}
              </TransformComponent>
            </TransformWrapper>
          </>
        ) : (
          renderImageContent()
        )}
      </ImageWrapper>
      <MapDrawer
        content={selectedPinContent || pinContentMapper[0]}
        isOpen={isDrawerVisible}
        isMobile={isMobile}
        onClose={handleDrawerClose}
      />
    </MapContainer>
  );
};
